import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orgApi } from '../services/api';
import { useAuth } from '../store/auth-context';

function useOrgId(): string {
  const { user } = useAuth();
  return user?.orgId ?? '';
}

/**
 * Per-user auth status against an org-level MCP install. This is Tier 2 in the
 * fallback chain — the calling user's own token, kept distinct from the org
 * service-account credential which is Tier 3.
 */
export function useOrgMcpAuthStatus(
  serverId: string | undefined,
  returnUrl?: string,
) {
  const orgId = useOrgId();
  const api = useMemo(() => orgApi(orgId).mcpServers, [orgId]);
  return useQuery({
    queryKey: ['org', orgId, 'mcp-servers', serverId, 'auth-status', returnUrl ?? ''],
    queryFn: () => api.authStatus(serverId!, returnUrl),
    enabled: !!orgId && !!serverId,
  });
}

export function useInvalidateOrgMcpAuthStatus() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const qc = useQueryClient();
  return (serverId: string) =>
    qc.invalidateQueries({
      queryKey: ['org', orgId, 'mcp-servers', serverId, 'auth-status'],
    });
}
